import React from 'react';
import axios from 'axios';

const ACTIVE_USERS_URL = `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/user-activity/active-users`;

/** Matches Admin Dashboard "Just now" (last activity < 1 minute). */
export const JUST_NOW_MS = 60 * 1000;

export function isJustNowActivity(lastActivity) {
  if (!lastActivity) return false;
  const diffMs = Date.now() - new Date(lastActivity).getTime();
  return Number.isFinite(diffMs) && diffMs >= 0 && diffMs < JUST_NOW_MS;
}

/**
 * Fetch user ids whose latest activity is "Just now" (Admin Dashboard meaning).
 * Uses the same /active-users endpoint, then filters by last_activity < 1 minute
 * so idle logged-in sessions are not treated as online.
 */
export async function fetchOnlineUserIds() {
  try {
    const response = await axios.get(ACTIVE_USERS_URL);
    const uniqueUsers = response.data.unique_users || [];
    return new Set(
      uniqueUsers
        .filter((u) => isJustNowActivity(u.last_activity))
        .map((u) => Number(u.id))
        .filter((id) => Number.isFinite(id))
    );
  } catch (err) {
    console.error('Failed to fetch active users for messaging:', err);
    return new Set();
  }
}

/**
 * Map eligible recipients to select options with online flag, online-first sort.
 */
export function buildRecipientOptions(recipients, onlineIds) {
  return (recipients || [])
    .map((u) => ({
      value: u.id,
      label: u.label,
      isOnline: onlineIds.has(Number(u.id)),
    }))
    .sort((a, b) => {
      if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
      return String(a.label).localeCompare(String(b.label));
    });
}

export const formatRecipientOptionLabel = (option) => (
  <span className="messages-recipient-option">
    {option.isAssistant && (
      <span className="messages-assistant-tag" title="Clinical Assistant">AI</span>
    )}
    {option.isOnline && (
      <span className="messages-online-dot" title="Online (just now)" aria-label="Online" />
    )}
    <span>{option.label}</span>
  </span>
);

export const OnlineDot = ({ title = 'Online (just now)' }) => (
  <span className="messages-online-dot" title={title} aria-label="Online" />
);
